import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
  getProject,
  updateProject,
  updateScene,
  listScenes,
  listSceneVersions,
  saveGeneratedScenes,
  insertSceneVersion,
  updateSceneVersion,
  startSceneVideo,
  pollSceneVideo,
  persistMedia,
  createProjectVersion,
  generateScript,
  generateScenes,
  formatDuration,
  PROJECT_TYPE_LABEL,
  aiProviderMeta,
  type ContentProject,
  type ContentScene,
} from '@/lib/contentStudio';
import SceneCard from '@/pages/dashboard/apps/content-studio/components/SceneCard';
import CharactersPanel from '@/pages/dashboard/apps/content-studio/components/CharactersPanel';
import BiblePanel from '@/pages/dashboard/apps/content-studio/components/BiblePanel';
import TimelineEditor from '@/pages/dashboard/apps/content-studio/components/TimelineEditor';
import FinalAssemblyPanel from '@/pages/dashboard/apps/content-studio/components/FinalAssemblyPanel';
import AudioPanel from '@/pages/dashboard/apps/content-studio/components/AudioPanel';
import MediaLibrary from '@/pages/dashboard/apps/content-studio/components/MediaLibrary';
import SubtitlesPanel from '@/pages/dashboard/apps/content-studio/components/SubtitlesPanel';
import CreativeAssistantPanel from '@/pages/dashboard/apps/content-studio/components/CreativeAssistantPanel';
import BrollPanel from '@/pages/dashboard/apps/content-studio/components/BrollPanel';
import ThumbnailsPanel from '@/pages/dashboard/apps/content-studio/components/ThumbnailsPanel';
import RepurposePanel from '@/pages/dashboard/apps/content-studio/components/RepurposePanel';

type TabKey = 'storyboard' | 'assistant' | 'characters' | 'bible' | 'timeline' | 'audio' | 'subtitles' | 'broll' | 'thumbnails' | 'repurpose' | 'media';

const TABS: { key: TabKey; label: string; icon: string }[] = [
  { key: 'storyboard', label: 'Storyboard', icon: 'ri-layout-grid-line' },
  { key: 'assistant', label: 'Assistant IA', icon: 'ri-sparkling-2-line' },
  { key: 'characters', label: 'Personnages', icon: 'ri-group-line' },
  { key: 'bible', label: 'Bible du projet', icon: 'ri-book-2-line' },
  { key: 'timeline', label: 'Timeline', icon: 'ri-movie-2-line' },
  { key: 'audio', label: 'Audio', icon: 'ri-music-2-line' },
  { key: 'subtitles', label: 'Sous-titres', icon: 'ri-subtract-line' },
  { key: 'broll', label: 'B-roll', icon: 'ri-movie-line' },
  { key: 'thumbnails', label: 'Miniatures', icon: 'ri-image-2-line' },
  { key: 'repurpose', label: 'Réseaux', icon: 'ri-share-forward-line' },
  { key: 'media', label: 'Médias', icon: 'ri-gallery-line' },
];

type Phase = 'loading' | 'understanding' | 'scripting' | 'splitting' | 'done' | 'error';

/** Statut d'une scène pendant la génération séquentielle des vidéos. */
type SceneStepStatus = 'pending' | 'running' | 'ready' | 'error';
interface SceneStep { id: number; number: number; status: SceneStepStatus; }

const STEPS: { key: Phase; label: string; icon: string }[] = [
  { key: 'understanding', label: 'Compréhension du besoin', icon: 'ri-brain-line' },
  { key: 'scripting', label: 'Génération du script', icon: 'ri-file-text-line' },
  { key: 'splitting', label: 'Découpage en scènes', icon: 'ri-scissors-cut-line' },
  { key: 'done', label: 'Storyboard prêt', icon: 'ri-layout-grid-line' },
];

const PHASE_ORDER: Phase[] = ['understanding', 'scripting', 'splitting', 'done'];

export default function ContentStudioProjectPage() {
  const { id } = useParams<{ id: string }>();
  const projectId = Number(id);
  const navigate = useNavigate();
  const { user } = useAuth();

  const [project, setProject] = useState<ContentProject | null>(null);
  const [scenes, setScenes] = useState<ContentScene[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');
  const [scriptDraft, setScriptDraft] = useState('');
  const [progressPct, setProgressPct] = useState(0);
  const [savingScript, setSavingScript] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [videoSteps, setVideoSteps] = useState<SceneStep[]>([]);
  const [savedFlash, setSavedFlash] = useState(false);
  const [tab, setTab] = useState<TabKey>('storyboard');
  const ranRef = useRef(false);

  // Le projet est aussi exposé via une réf : la fonction de génération lit les
  // données à jour SANS dépendre de l'état « project ». Sans cela, chaque mise
  // à jour du projet recréait la fonction de chargement et provoquait une boucle
  // (la page clignotait : tout s'affichait puis disparaissait).
  const projectRef = useRef<ContentProject | null>(null);
  useEffect(() => { projectRef.current = project; }, [project]);

  const loadScenes = useCallback(async () => {
    const list = await listScenes(projectId);
    setScenes(list);
    return list;
  }, [projectId]);

  const runGeneration = useCallback(async (regenerate = false) => {
    const current = projectRef.current;
    setError(null);
    setProgressPct(8);
    setPhase('understanding');
    try {
      // Étape 1 — script (appel IA court, isolé pour rester sous le délai Edge).
      setPhase('scripting');
      setProgressPct(30);
      const result = await generateScript({
        prompt: current?.prompt || undefined,
        title: current?.name,
        language: current?.language || 'fr',
        duration: current?.duration_seconds || 60,
        style: current?.style || undefined,
        narration: current?.narration,
        targetAudience: current?.target_audience || undefined,
      });

      if (!result.script) {
        setPhase('error');
        setError("Le moteur IA n'a pas renvoyé de script. Vérifiez la configuration IA de la plateforme.");
        await updateProject(projectId, { status: 'error' });
        return;
      }

      // On enregistre le script IMMÉDIATEMENT : rien n'est perdu si le
      // découpage en scènes échoue ensuite.
      const nextVersion = (current?.script_version || 1) + (regenerate ? 1 : 0);
      await updateProject(projectId, {
        status: 'generating',
        script: result.script,
        word_count: result.wordCount,
        estimated_duration: result.estimatedDuration,
        script_version: nextVersion || 1,
        visual_context: result.hook || null,
        // Transparence : on mémorise le moteur IA qui a réellement produit le
        // script (diagnostic en cas de baisse de qualité ou de panne).
        ai_provider: result.provider || current?.ai_provider || null,
        name: regenerate ? current?.name : (result.title || current?.name || 'Nouveau projet'),
      });
      await createProjectVersion(projectId, result.script, nextVersion || 1, regenerate ? 'Régénération' : 'Version initiale');
      setProgressPct(62);

      // Étape 2 — découpage en scènes (second appel IA court, indépendant).
      setPhase('splitting');
      const scenes = await generateScenes({
        script: result.script,
        language: current?.language || 'fr',
        style: current?.style || undefined,
      });
      await saveGeneratedScenes(projectId, scenes);
      setProgressPct(92);

      await updateProject(projectId, { status: 'ready' });
      const fresh = await getProject(projectId);
      setProject(fresh);
      setScriptDraft(fresh?.script || result.script);
      await loadScenes();
      setProgressPct(100);
      setPhase('done');
    } catch (e) {
      setPhase('error');
      setError(e instanceof Error ? e.message : 'La génération a échoué');
      try { await updateProject(projectId, { status: 'error' }); } catch { /* ignore */ }
    }
  }, [projectId, loadScenes]);

  const load = useCallback(async () => {
    if (!projectId || Number.isNaN(projectId)) {
      setError('Projet introuvable');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const p = await getProject(projectId);
      if (!p) {
        setError('Projet introuvable');
        setLoading(false);
        return;
      }
      projectRef.current = p;
      setProject(p);
      setScriptDraft(p.script || '');
      const list = await listScenes(projectId);
      setScenes(list);
      setPhase(list.length > 0 ? 'done' : (p.status === 'generating' ? 'understanding' : 'done'));

      // Génération automatique si le projet vient d'être créé (aucune scène).
      if (list.length === 0 && (p.prompt || p.script) && !ranRef.current) {
        ranRef.current = true;
        await runGeneration(false);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
      setPhase('error');
    } finally {
      setLoading(false);
    }
  }, [projectId, runGeneration]);

  useEffect(() => { load(); }, [load]);

  const handleSaveScript = async () => {
    if (!project || savingScript) return;
    setSavingScript(true);
    try {
      const nextVersion = (project.script_version || 1) + 1;
      await updateProject(project.id, { script: scriptDraft, script_version: nextVersion });
      await createProjectVersion(project.id, scriptDraft, nextVersion, 'Modification manuelle');
      const fresh = await getProject(project.id);
      setProject(fresh);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } finally {
      setSavingScript(false);
    }
  };

  // File de génération séquentielle des VIDÉOS : on lance une scène, on attend
  // qu'elle soit terminée (le moteur vidéo est asynchrone), puis la suivante.
  const handleGenerateAllVideos = async () => {
    if (bulkRunning || !project) return;
    setBulkRunning(true);
    setError(null);
    try {
      const pending = scenes.filter((s) => !s.locked && s.status !== 'ready');
      // État de progression : une ligne par scène (en génération / terminée…).
      setVideoSteps(pending.map((s) => ({ id: s.id, number: s.number, status: 'pending' })));
      for (const s of pending) {
        setVideoSteps((prev) => prev.map((v) => (v.id === s.id ? { ...v, status: 'running' } : v)));
        // eslint-disable-next-line no-await-in-loop
        const current = await listSceneVersions(s.id);
        const nextVersion = (current[0]?.version || 0) + 1;
        // eslint-disable-next-line no-await-in-loop
        const v = await insertSceneVersion(s.id, {
          version: nextVersion,
          prompt: s.visual_prompt || s.script || '',
          status: 'processing',
          media_kind: 'video',
        });
        if (!v) {
          setVideoSteps((prev) => prev.map((x) => (x.id === s.id ? { ...x, status: 'error' } : x)));
          continue;
        }
        // eslint-disable-next-line no-await-in-loop
        await updateScene(s.id, { status: 'generating', active_version_id: v.id });
        // eslint-disable-next-line no-await-in-loop
        await loadScenes();
        // eslint-disable-next-line no-await-in-loop
        const { requestId } = await startSceneVideo({
          prompt: s.visual_prompt || s.script || '',
          duration: s.duration,
          aspectRatio: project.aspect_ratio,
          resolution: '720p',
        });
        // eslint-disable-next-line no-await-in-loop
        await updateSceneVersion(v.id, { request_id: requestId, status: 'processing' });
        // Attente active du résultat de la scène courante.
        let finished = false;
        while (!finished) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 8000));
          // eslint-disable-next-line no-await-in-loop
          const state = await pollSceneVideo(requestId);
          if (state.status === 'done' && state.videoUrl) {
            // Rendre le lien permanent (les URLs xAI sont temporaires).
            let finalUrl = state.videoUrl;
            try {
              // eslint-disable-next-line no-await-in-loop
              finalUrl = await persistMedia(state.videoUrl, `scene-${s.number}-v${nextVersion}.mp4`, 'content-studio/scenes', 'video/mp4');
            } catch { finalUrl = state.videoUrl; }
            // eslint-disable-next-line no-await-in-loop
            await updateSceneVersion(v.id, { video_url: finalUrl, status: 'ready' });
            // eslint-disable-next-line no-await-in-loop
            await updateScene(s.id, { status: 'ready' });
            setVideoSteps((prev) => prev.map((x) => (x.id === s.id ? { ...x, status: 'ready' } : x)));
            finished = true;
          } else if (state.status === 'failed' || state.status === 'expired') {
            // eslint-disable-next-line no-await-in-loop
            await updateSceneVersion(v.id, { status: 'error', error: state.error || 'Échec de la génération' });
            // eslint-disable-next-line no-await-in-loop
            await updateScene(s.id, { status: 'error' });
            setVideoSteps((prev) => prev.map((x) => (x.id === s.id ? { ...x, status: 'error' } : x)));
            finished = true;
          }
        }
        // eslint-disable-next-line no-await-in-loop
        await loadScenes();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'La génération vidéo a échoué');
    } finally {
      setBulkRunning(false);
    }
  };

  const reloadScenes = useCallback(async () => { await loadScenes(); }, [loadScenes]);

  const generating = phase !== 'done' && phase !== 'error' && phase !== 'loading';
  const currentStepIndex = PHASE_ORDER.indexOf(phase);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <i className="ri-loader-4-line animate-spin text-2xl text-accent-500"></i>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6 max-w-2xl mx-auto text-center py-20">
        <i className="ri-error-warning-line text-4xl text-red-400 mb-3"></i>
        <p className="text-foreground-600 mb-4">{error || 'Projet introuvable'}</p>
        <button
          onClick={() => navigate('/dashboard/content-studio')}
          className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600"
        >
          Retour au studio
        </button>
      </div>
    );
  }

  const readyScenes = scenes.filter((s) => s.status === 'ready').length;
  const providerInfo = aiProviderMeta(project.ai_provider);

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      {/* Fil d'ariane */}
      <Link
        to="/dashboard/content-studio"
        className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors mb-4 cursor-pointer"
      >
        <i className="ri-arrow-left-line"></i>
        Content Studio
      </Link>

      {/* En-tête projet */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
        <div className="min-w-0 flex-1">
          <input
            value={project.name}
            onChange={(e) => setProject({ ...project, name: e.target.value })}
            onBlur={() => updateProject(project.id, { name: project.name })}
            className="w-full text-xl md:text-2xl font-bold font-heading text-foreground-950 bg-transparent border-b border-transparent hover:border-background-200/70 focus:border-accent-300 focus:outline-none truncate"
          />
          <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-background-100">
              <i className="ri-price-tag-3-line"></i>{PROJECT_TYPE_LABEL[project.type] || project.type}
            </span>
            <span className="inline-flex items-center gap-1"><i className="ri-timer-line"></i>Objectif {formatDuration(project.duration_seconds)}</span>
            <span className="inline-flex items-center gap-1"><i className="ri-aspect-ratio-line"></i>{project.aspect_ratio}</span>
            <span className="inline-flex items-center gap-1"><i className="ri-translate-2"></i>{project.language.toUpperCase()}</span>
            {project.word_count ? <span className="inline-flex items-center gap-1"><i className="ri-text"></i>{project.word_count} mots</span> : null}
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium ${providerInfo.className}`}
              title="Moteur IA ayant produit ce contenu"
            >
              <i className={providerInfo.icon}></i>Moteur IA : {providerInfo.label}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => runGeneration(true)}
          disabled={generating}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-100 border border-background-200/70 text-sm font-medium text-foreground-700 whitespace-nowrap hover:bg-background-200/70 disabled:opacity-50 transition-colors cursor-pointer"
        >
          <i className={`ri-refresh-line ${generating ? 'animate-spin' : ''}`}></i>
          Régénérer tout le projet
        </button>
      </div>

      {/* Étapes de génération */}
      {generating && (
        <div className="mb-6 bg-background-50 border border-primary-200/60 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-foreground-800 flex items-center gap-2">
              <i className="ri-loader-4-line animate-spin text-primary-600"></i>
              Génération en cours… veuillez patienter
            </p>
            <span className="text-xs font-bold text-primary-700">{progressPct}%</span>
          </div>
          <div className="h-2 w-full rounded-full bg-background-200/70 overflow-hidden mb-4">
            <div
              className="h-full bg-primary-500 rounded-full transition-all duration-700 ease-out"
              style={{ width: `${Math.max(6, progressPct)}%` }}
            ></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {STEPS.map((step, i) => {
              const done = currentStepIndex > i;
              const active = currentStepIndex === i;
              return (
                <div key={step.key} className={`flex items-center gap-2 px-3 py-2 rounded-lg ${active ? 'bg-primary-50' : done ? 'bg-accent-50' : 'bg-background-100'}`}>
                  <i className={`${done ? 'ri-checkbox-circle-line text-accent-600' : active ? `${step.icon} text-primary-600` : `${step.icon} text-foreground-300`}`}></i>
                  <span className={`text-xs font-medium ${done ? 'text-accent-700' : active ? 'text-primary-700' : 'text-foreground-400'}`}>{step.label}</span>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-foreground-400 mt-3 flex items-center gap-1.5">
            <i className="ri-time-line"></i>
            La rédaction du script puis le découpage en scènes peuvent prendre une minute. Ne fermez pas cette page.
          </p>
        </div>
      )}

      {error && phase === 'error' && (
        <div className="mb-6 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-4 py-3 rounded-lg">
          <i className="ri-error-warning-line"></i>{error}
        </div>
      )}

      {/* Onglets du projet */}
      <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mb-6 overflow-x-auto">
        {TABS.map((tb) => (
          <button
            key={tb.key}
            type="button"
            onClick={() => setTab(tb.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
              tab === tb.key ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            <i className={tb.icon}></i>{tb.label}
          </button>
        ))}
      </div>

      {tab === 'storyboard' && (
      <>
      {/* Script + stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-2 bg-background-50 border border-background-200/70 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-foreground-900 flex items-center gap-2">
              <i className="ri-file-text-line text-foreground-500"></i>
              Script
              <span className="text-[11px] font-normal text-foreground-400">v{project.script_version}</span>
            </h3>
            <div className="flex items-center gap-2">
              {savedFlash && <span className="text-xs text-accent-600 flex items-center gap-1"><i className="ri-check-line"></i>Enregistré</span>}
              <button
                type="button"
                onClick={handleSaveScript}
                disabled={savingScript || scriptDraft === (project.script || '')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-40 transition-colors cursor-pointer"
              >
                {savingScript ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
                Enregistrer
              </button>
            </div>
          </div>
          <textarea
            value={scriptDraft}
            onChange={(e) => setScriptDraft(e.target.value)}
            rows={14}
            placeholder="Le script généré apparaîtra ici…"
            className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 leading-relaxed focus:outline-none focus:border-accent-300 resize-y"
          />
          <p className="text-[11px] text-foreground-400 mt-1.5 flex items-center gap-1">
            <i className="ri-information-line"></i>
            Chaque enregistrement crée une nouvelle version (l'ancienne n'est jamais supprimée).
          </p>
        </div>

        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
          <h3 className="text-sm font-bold text-foreground-900 mb-3 flex items-center gap-2">
            <i className="ri-bar-chart-box-line text-foreground-500"></i>Estimation
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-foreground-500">Mots</span>
              <span className="text-sm font-bold text-foreground-900">{scriptDraft.split(/\s+/).filter(Boolean).length}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-foreground-500">Durée estimée</span>
              <span className="text-sm font-bold text-foreground-900">{formatDuration(Math.round(scriptDraft.split(/\s+/).filter(Boolean).length / 2.3))}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-foreground-500">Scènes</span>
              <span className="text-sm font-bold text-foreground-900">{scenes.length}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-foreground-500">Vidéos prêtes</span>
              <span className="text-sm font-bold text-accent-700">{readyScenes}/{scenes.length}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-foreground-500">Moteur IA utilisé</span>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${providerInfo.className}`}>
                <i className={providerInfo.icon}></i>{providerInfo.label}
              </span>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-background-200/70">
            <p className="text-[11px] text-foreground-400 flex items-center gap-1.5">
              <i className="ri-sparkling-2-line text-accent-500"></i>
              Script & scènes générés par IA à partir de votre idée.
            </p>
          </div>
        </div>
      </div>

      {/* Scènes */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-bold font-heading text-foreground-950 flex items-center gap-2">
          <i className="ri-layout-grid-line text-foreground-500"></i>
          Scènes
          <span className="text-xs font-normal text-foreground-400">({scenes.length})</span>
        </h3>
        {scenes.length > 0 && (
          <button
            type="button"
            onClick={handleGenerateAllVideos}
            disabled={bulkRunning || readyScenes === scenes.length}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-accent-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-accent-600 disabled:opacity-50 transition-colors cursor-pointer"
          >
            {bulkRunning ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-play-circle-line"></i>}
            {bulkRunning ? 'Génération…' : 'Générer les vidéos'}
          </button>
        )}
      </div>

      {/* Progression de la génération vidéo : une étape visible par scène */}
      {(bulkRunning || videoSteps.length > 0) && (
        <div className="mb-5 bg-background-50 border border-accent-200/60 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-foreground-800 flex items-center gap-2">
              <i className={`ri-loader-4-line text-accent-600 ${bulkRunning ? 'animate-spin' : ''}`}></i>
              Génération des vidéos {bulkRunning ? 'en cours…' : 'terminée'}
            </p>
            <span className="text-xs font-bold text-accent-700">
              {videoSteps.filter((v) => v.status === 'ready').length}/{videoSteps.length}
            </span>
          </div>
          <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
            {videoSteps.map((v) => (
              <div
                key={v.id}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${
                  v.status === 'ready' ? 'bg-accent-50 text-accent-700'
                    : v.status === 'running' ? 'bg-primary-50 text-primary-700'
                      : v.status === 'error' ? 'bg-red-50 text-red-600'
                        : 'bg-background-100 text-foreground-500'
                }`}
              >
                <i
                  className={
                    v.status === 'ready' ? 'ri-checkbox-circle-line'
                      : v.status === 'running' ? 'ri-loader-4-line animate-spin'
                        : v.status === 'error' ? 'ri-error-warning-line'
                          : 'ri-time-line'
                  }
                ></i>
                <span className="font-medium">Scène {v.number}</span>
                <span className="ml-auto">
                  {v.status === 'ready' ? 'terminée'
                    : v.status === 'running' ? 'en génération…'
                      : v.status === 'error' ? 'échec'
                        : 'en attente'}
                </span>
              </div>
            ))}
          </div>
          {bulkRunning && (
            <p className="text-[11px] text-foreground-400 mt-3 flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              Chaque scène est générée l&apos;une après l&apos;autre puis assemblée. Ne fermez pas cette page.
            </p>
          )}
        </div>
      )}

      {scenes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 bg-background-50 border border-background-200/70 rounded-xl text-center">
          <i className="ri-scissors-cut-line text-3xl text-foreground-300 mb-3"></i>
          <p className="text-sm text-foreground-600">Aucune scène pour l'instant.</p>
          <p className="text-xs text-foreground-400 mt-1">Le découpage apparaîtra ici après la génération du script.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {scenes.map((s) => (
            <SceneCard key={s.id} scene={s} project={project} onChanged={reloadScenes} />
          ))}
        </div>
      )}
      </>
      )}

      {tab === 'assistant' && (
        <CreativeAssistantPanel project={project} scenes={scenes} onScenesChanged={reloadScenes} />
      )}
      {tab === 'characters' && <CharactersPanel projectId={projectId} />}
      {tab === 'bible' && (
        <BiblePanel
          project={project}
          onSaved={(b) => setProject({ ...project, bible: b as unknown as Record<string, unknown> })}
        />
      )}
      {tab === 'timeline' && (
        <div className="space-y-8">
          <FinalAssemblyPanel project={project} scenes={scenes} />
          <TimelineEditor projectId={projectId} scenes={scenes} />
        </div>
      )}
      {tab === 'audio' && <AudioPanel projectId={projectId} scenes={scenes} language={project.language} />}
      {tab === 'subtitles' && (
        <SubtitlesPanel projectId={projectId} scenes={scenes} language={project.language} />
      )}
      {tab === 'broll' && <BrollPanel project={project} scenes={scenes} />}
      {tab === 'thumbnails' && <ThumbnailsPanel project={project} />}
      {tab === 'repurpose' && <RepurposePanel project={project} scenes={scenes} />}
      {tab === 'media' && (
        <MediaLibrary ownerId={project.owner_id ?? user?.id ?? 0} projectId={projectId} scenes={scenes} />
      )}
    </div>
  );
}